import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, TrendingUp, TrendingDown, PieChart, AlertTriangle, Wallet, BarChart3 } from "lucide-react";
import { Transaction, Category } from "@/types/database";
import { DashboardCharts } from "@/components/dashboard/DashboardCharts";
import { MonthYearPicker } from "@/components/common/MonthYearPicker";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { PendingExpensesReport } from "@/components/reports/PendingExpensesReport";
import { CategoryExpensesReport } from "@/components/reports/CategoryExpensesReport";
import { CategoryIncomeReport } from "@/components/reports/CategoryIncomeReport";
import { AnnualBalanceReport } from "@/components/reports/AnnualBalanceReport";
import { YearlyExpensesReport } from "@/components/reports/YearlyExpensesReport";
import { YearlyIncomeReport } from "@/components/reports/YearlyIncomeReport";
import { MonthlyExpensesReport } from "@/components/reports/MonthlyExpensesReport";
import { MonthlyIncomeReport } from "@/components/reports/MonthlyIncomeReport";
import { MonthlyBalanceReport } from "@/components/reports/MonthlyBalanceReport";
import { CashFlowReport } from "@/components/reports/CashFlowReport";
import { TrendsAnalysisReport } from "@/components/reports/TrendsAnalysisReport";
import { YearlyComparisonReport } from "@/components/reports/YearlyComparisonReport";
import { NetWorthReport } from "@/components/reports/NetWorthReport";
import { useIsMobile } from "@/hooks/use-mobile";

export type ReportType = 'monthly' | 'balance' | 'monthly-balance' | 'pending' | 'category-expenses' | 'category-income' | 'monthly-expenses' | 'monthly-income' | 'yearly-income' | 'cash-flow' | 'net-worth' | 'trends-analysis' | 'yearly-comparison';

interface ReportsProps {
  transactions: Transaction[];
  selectedDate?: Date;
  onBackToDashboard?: () => void;
  onDateChange?: (date: Date) => void;
  initialActiveReport?: ReportType;
  onNavigateToTransactions?: (categoryFilter?: string, month?: number, year?: number) => void;
  categories?: Category[];
}

export function Reports({
  transactions,
  selectedDate,
  onBackToDashboard,
  onDateChange,
  initialActiveReport,
  onNavigateToTransactions,
  categories = []
}: ReportsProps) {
  
  const isMobile = useIsMobile();
  const [activeReport, setActiveReport] = useState<ReportType>(initialActiveReport ?? 'monthly');
  const [onlyPending, setOnlyPending] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Scroll to top when component mounts or when initialActiveReport changes
  useEffect(() => {
    if (initialActiveReport) {
      setActiveReport(initialActiveReport);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [initialActiveReport]);

  const reportTypes = {
    expenses: [
      {
        id: 'monthly' as const,
        title: 'Despesas por ano',
        icon: Calendar,
        description: 'Visualize suas despesas organizadas por ano'
      },
      {
        id: 'monthly-expenses' as const,
        title: 'Despesas do mês',
        icon: Calendar,
        description: 'Análise detalhada das despesas mensais'
      },
      {
        id: 'pending' as const,
        title: 'Despesas Pendentes',
        icon: AlertTriangle,
        description: 'Despesas que ainda não foram pagas'
      },
      {
        id: 'category-expenses' as const,
        title: 'Despesas por Categoria',
        icon: PieChart,
        description: 'Análise detalhada das despesas por categoria'
      }
    ],
    income: [
      {
        id: 'yearly-income' as const,
        title: 'Receitas por ano',
        icon: Calendar,
        description: 'Visualize suas receitas organizadas por ano'
      },
      {
        id: 'monthly-income' as const,
        title: 'Receitas do mês',
        icon: Calendar,
        description: 'Análise detalhada das receitas mensais'
      },
      {
        id: 'category-income' as const,
        title: 'Receitas por Categoria',
        icon: PieChart,
        description: 'Análise das receitas organizadas por categoria'
      }
    ],
    balance: [
      {
        id: 'balance' as const,
        title: 'Balanço anual',
        icon: TrendingUp,
        description: 'Comparação entre receitas e despesas anuais por mês'
      },
      {
        id: 'monthly-balance' as const,
        title: 'Balanço mensal',
        icon: Calendar,
        description: 'Evolução diária do saldo no mês selecionado'
      },
      {
        id: 'cash-flow' as const,
        title: 'Fluxo de caixa',
        icon: TrendingUp,
        description: 'Análise do fluxo de entrada e saída de dinheiro'
      }
    ],
    analysis: [
      {
        id: 'yearly-comparison' as const,
        title: 'Comparativo anual',
        icon: BarChart3,
        description: 'Compare gastos e receitas entre diferentes anos'
      },
      {
        id: 'trends-analysis' as const,
        title: 'Análise de tendências',
        icon: TrendingUp,
        description: 'Identifique padrões e tendências nos seus gastos'
      },
      {
        id: 'net-worth' as const,
        title: 'Patrimônio líquido',
        icon: Wallet,
        description: 'Evolução do seu patrimônio ao longo do tempo'
      }
    ]
  };

  const reportCategories = [
    { id: 'expenses', title: 'Despesas', icon: TrendingDown, color: 'text-red-500' },
    { id: 'income', title: 'Receitas', icon: TrendingUp, color: 'text-green-500' },
    { id: 'balance', title: 'Balanços', icon: Wallet, color: 'text-blue-500' },
    { id: 'analysis', title: 'Análises', icon: BarChart3, color: 'text-purple-500' }
  ];

  const displayTransactions = onlyPending ? transactions.filter(t => t.tipo === 'saida' && !t.foi_paga) : transactions;

  const handleTabChange = (tab: string) => {
    // Handle tab navigation if needed
  };

  const handleTransactionSuccess = () => {
    // Handle transaction success if needed
  };

  return (
    <div className={`${!isMobile ? 'p-3 sm:p-4 md:p-6' : 'px-4'}`}>
      <div className={`${!isMobile ? 'max-w-7xl mx-auto' : ''}`}>
        {/* Category Selector - Compact Horizontal Layout */}
        <div>
          <div className={`flex ${isMobile ? 'flex-wrap gap-2 justify-center' : 'flex-wrap gap-2 justify-center'}`}>
            {reportCategories.map((category) => {
              const Icon = category.icon;
              const isExpanded = expandedCategory === category.id;
              return (
                <div key={category.id} className="relative">
                  <Button
                    variant={isExpanded ? "default" : "outline"}
                    size={isMobile ? "sm" : "default"}
                    className={`${isMobile ? 'min-w-[80px]' : 'min-w-[120px]'} transition-all hover:shadow-sm`}
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  >
                    <Icon className={`w-4 h-4 ${isMobile ? 'mr-1' : 'mr-1'} ${category.color}`} />
                    <span className="text-sm">{category.title}</span>
                    <div className={`ml-2 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      ▼
                    </div>
                  </Button>
                  
                  {/* Dropdown for reports - Desktop and Mobile */}
                  {isExpanded && (
                    <div className={`absolute top-full ${isMobile ? 'left-0 right-0' : 'left-0'} mt-2 ${isMobile ? 'w-full' : 'w-72'} bg-background border rounded-md shadow-lg z-50`}>
                      <div className="p-2 space-y-1">
                        {reportTypes[expandedCategory as keyof typeof reportTypes].map((report) => {
                          const ReportIcon = report.icon;
                          return (
                            <button
                              key={report.id}
                              className={`w-full text-left p-3 rounded-sm hover:bg-accent transition-colors ${
                                activeReport === report.id ? 'bg-primary/10 text-primary' : ''
                              }`}
                              onClick={() => {
                                setActiveReport(report.id);
                                setExpandedCategory(null);
                              }}
                            >
                              <div className="flex items-center gap-3">
                                 <ReportIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                 <div className="flex-1 min-w-0">
                                   <div className="font-medium text-sm truncate">
                        {isMobile ? (
                          report.title === 'Despesas por ano' ? 'Por ano' :
                          report.title === 'Despesas do mês' ? 'Por mês' :
                          report.title === 'Despesas Pendentes' ? 'Pendentes' :
                          report.title === 'Despesas por Categoria' ? 'Categorias' :
                          report.title === 'Receitas por ano' ? 'Por ano' :
                          report.title === 'Receitas do mês' ? 'Por mês' :
                          report.title === 'Receitas por Categoria' ? 'Categorias' :
                          report.title === 'Balanço anual' ? 'Anual' :
                          report.title === 'Balanço mensal' ? 'Mensal' :
                          report.title === 'Fluxo de caixa' ? 'Fluxo' :
                          report.title === 'Patrimônio líquido' ? 'Patrimônio' :
                          report.title === 'Comparativo anual' ? 'Comparativo' :
                          report.title === 'Análise de tendências' ? 'Tendências' :
                          report.title
                        ) : report.title}
                      </div>
                                   {!isMobile && (
                                     <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{report.description}</div>
                                   )}
                                 </div>
                               </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
      </div>

        {/* Report Content */}
        <Card className="mt-6"> 
          <CardContent className="p-6">
            {(() => {
              switch (activeReport) {
                case 'monthly':
                  return <YearlyExpensesReport transactions={transactions as Transaction[]} selectedDate={selectedDate} categories={categories} />;
                case 'monthly-expenses':
                  return <MonthlyExpensesReport transactions={transactions as Transaction[]} selectedDate={selectedDate} categories={categories} />;
                case 'monthly-income':
                  return <MonthlyIncomeReport transactions={transactions as Transaction[]} selectedDate={selectedDate} categories={categories} />;
                case 'yearly-income':
                  return <YearlyIncomeReport transactions={transactions as Transaction[]} selectedDate={selectedDate} categories={categories} />;
                case 'balance':
                  return <AnnualBalanceReport 
                    transactions={transactions as Transaction[]} 
                    selectedDate={selectedDate} 
                    onDateChange={onDateChange} 
                    onNavigateToTransactions={(month, year) => onNavigateToTransactions?.(undefined, month, year)} 
                  />;
                case 'monthly-balance':
                  return <MonthlyBalanceReport transactions={transactions as Transaction[]} selectedDate={selectedDate} onDateChange={onDateChange} />;
                case 'pending':
                  return <PendingExpensesReport transactions={transactions as Transaction[]} selectedDate={selectedDate} />;
                case 'category-expenses':
                  return <CategoryExpensesReport 
                    transactions={transactions as Transaction[]} 
                    selectedDate={selectedDate}
                    onCategoryClick={(categoryName, month, year) => {
                      onNavigateToTransactions?.(categoryName, month, year);
                    }}
                  />;
                case 'category-income':
                  return <CategoryIncomeReport 
                    transactions={transactions as Transaction[]} 
                    selectedDate={selectedDate}
                    onCategoryClick={(categoryName, month, year) => {
                      onNavigateToTransactions?.(categoryName, month, year);
                    }}
                  />;
                case 'cash-flow':
                  return <CashFlowReport transactions={transactions as Transaction[]} selectedDate={selectedDate} />;
                case 'trends-analysis':
                  return <TrendsAnalysisReport transactions={transactions as Transaction[]} selectedDate={selectedDate} />;
                case 'yearly-comparison':
                  return <YearlyComparisonReport transactions={transactions as Transaction[]} selectedDate={selectedDate} />;
                case 'net-worth':
                  return <NetWorthReport transactions={transactions as Transaction[]} selectedDate={selectedDate} />;
                default:
                  return <DashboardCharts transactions={displayTransactions} selectedDate={selectedDate} />;
              }
            })()} 
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
